import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Status mapping types
export type StatusVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'success'
  | 'warning'
  | 'info'
  | 'tertiary';

export interface StatusMapping {
  text: string;
  variant: StatusVariant;
}

// Unified status mapping function
export function getStatusMapping(
  status: string,
  isDeleted: boolean,
): StatusMapping {
  if (isDeleted) {
    return { text: 'Gelöscht', variant: 'destructive' };
  }
  switch (status) {
    case 'OFFEN':
      return { text: 'Offen', variant: 'info' };
    case 'GEPLANT':
      return { text: 'Geplant', variant: 'warning' };
    case 'LAUFEND':
      return { text: 'Laufend', variant: 'secondary' };
    case 'ERFOLGREICH':
      return { text: 'Erledigt', variant: 'success' };
    case 'FEHLER':
      return { text: 'Fehler', variant: 'destructive' };
    case 'WARTEND':
      return { text: 'Wartend', variant: 'warning' };
    case 'PAUSIERT':
      return { text: 'Pausiert', variant: 'warning' };
    case 'ZURUECKGESTELLT':
      return { text: 'Zurückgestellt', variant: 'tertiary' };
    default:
      return { text: 'Unbekannt', variant: 'secondary' };
  }
}

// Generic status grouping utility
export interface GroupedStatusItem {
  value: string;
  text: string;
  variant: StatusVariant;
}

export interface GroupedStatusWithCount extends GroupedStatusItem {
  count: number;
}

// Universal function that groups statuses by variant
// Takes normalized input: array of { value: string, count?: number } objects
function groupStatusesByVariantCore<
  T extends { value: string; count?: number },
>(items: T[]): Record<string, (T & GroupedStatusItem)[]> {
  return items.reduce(
    (groups, item) => {
      const { text, variant } = getStatusMapping(
        item.value,
        item.value === 'GELOESCHT',
      );

      if (!groups[variant]) {
        groups[variant] = [];
      }

      groups[variant].push({
        ...item,
        text,
        variant: variant as StatusVariant,
      });

      return groups;
    },
    {} as Record<string, (T & GroupedStatusItem)[]>,
  );
}

// Adapter function for grouping status options (used by filters)
export function groupStatusOptionsByVariant<T extends { value: string }>(
  items: T[],
): Record<string, (T & GroupedStatusItem)[]> {
  return groupStatusesByVariantCore(items);
}

// Adapter function for grouping status clustering data
export function groupStatusClusteringByVariant(
  clustering: Record<string, number>,
): Record<string, GroupedStatusWithCount[]> {
  // Format clustering data into normalized format
  const normalizedItems = Object.entries(clustering)
    .filter(([, count]) => count > 0)
    .map(([value, count]) => ({ value, count }));

  return groupStatusesByVariantCore(normalizedItems) as Record<
    string,
    GroupedStatusWithCount[]
  >;
}

export const getErrResponseDetail = (err: unknown): string => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = err.response;
    if (response && typeof response === 'object' && 'data' in response) {
      const data = response.data;
      if (
        data &&
        typeof data === 'object' &&
        'detail' in data &&
        typeof data.detail === 'string'
      )
        return data.detail;
    }
  }
  return err instanceof Error
    ? err.message
    : 'Fehler beim Verarbeiten des Vorgangs.';
};

export const getOnSaveErrorMessage = (err): string[] => {
  const responseData = err.response?.data;
  if (Array.isArray(responseData?.data)) {
    return responseData.data.map((fieldError) => {
      return (
        fieldError.value +
        ' ist als ' +
        translateFormErrorDokumenttypField(fieldError.field) +
        ' bereits vergeben. '
      );
    });
  }
  if (Array.isArray(responseData?.error)) {
    return responseData.error.map((issue) => issue.message);
  }

  return [];
};

export const translateFormErrorDokumenttypField = (field: string) => {
  switch (field) {
    case 'eventName':
      return 'Event Name';
    case 'dokumentTyp':
      return 'Dokumenttyp';
    default:
      return '';
  }
};

export const uInt8ArrayFromBase64 = (base64: string) => {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
};

export const base64ToString = (base64: string) => {
  const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(binary);
};
